/**
 * postcardManager — Sprint 4A
 * Secret gems made known (the constellation) + the city-postcard reward.
 *
 * Two ideas live here, both offline-first and additive:
 *
 *   1. CONSTELLATION — make it KNOWN that a city hides gems, without spoiling
 *      WHERE. A city's gem total comes from the authored content (quirky POIs
 *      tagged with that city). "Found" = a gem surfaced near the traveller
 *      (≤150 m → already recorded in the trip's surfacedPois by useProximity).
 *      We deliberately REUSE surfacedPois as the per-trip "found gems" set — no
 *      duplicate state to drift (confirmed: foundHiddenPois ≡ surfacedPois).
 *
 *   2. POSTCARD — when EVERY gem of a city is found (evaluated CROSS-TRIP: the
 *      gems may be discovered in different trips to the same place), the city's
 *      postcard unlocks. The digital unlock is free, instant, local — the merit
 *      is the discovery, never points. cityPostcards is a profile-level record
 *      (store userProfile, dedicated key 'postcards'), mirroring atlasManager:
 *      it is reconstructible from the trips, so .trip portability stays intact
 *      and a restore can recompute it.
 *
 * No network for the digital unlock. MySQL only enters if the user asks for the
 * physical postcard (see src/components/PostcardCollection — the pipeline).
 */
import storageManager from './storageManager';
import { poiSlug } from './reveal';
import { loadCityHiddenGems } from './loadTripPois';
import { getCountryFile } from './countryUtils';

// ── Constants (values confirmed by Érico) ──────────────────────────────────
export const POSTCARD_DIGITAL_FREE = true;      // digital unlock is free (merit = discovering)
export const POSTCARD_PHYSICAL = 'link';        // 'link' (redeem on losdias.xyz with a code) | 'off'
export const CONSTELLATION_REVEAL = 'count';    // show HOW MANY exist, never WHERE

// A free-earned postcard is redeemed for a PRINTED one on losdias.xyz: the unlock
// mints a code locally and the app links out carrying it. The app holds NO API
// secret (it's a public PWA), so it never registers the code itself — losdias.xyz
// validates the code at redemption (first-seen → flagged for admin approval, which
// is the "verified agent" gate). The redeem flow + the order/address all live on
// losdias.xyz; this side is pure offline mint + link.
// Language is a PATH PREFIX (e.g. /es/postal, /en/postal): `${base}/${lang}/postal?code=…&city=…`.
export const POSTCARD_REDEEM_BASE = 'https://www.losdias.xyz';

const POSTCARDS_KEY = 'postcards';

/** Resolve a city's illustration — the SAME art the app already ships at
 *  public/TRIP/img/<folder>/<folder>-<city>.webp — to a RELATIVE path under
 *  TRIP/img (e.g. "albania/albania-berat.webp"). The folder derives from the
 *  country's POI file id. Stored on the postcard so the redeem link can carry it
 *  to losdias.xyz. Returns null if it can't be resolved. */
let _imgIndexPromise = null;
/** citySlug → real relative path under TRIP/img (scanned from the folder, so it
 *  survives the folder/filename irregularities like moldavia/ or bosnia-…). */
function loadImgIndex() {
  if (!_imgIndexPromise) {
    _imgIndexPromise = fetch('./TRIP/img/index.json')
      .then(r => r.ok ? r.json() : {}).catch(() => ({}));
  }
  return _imgIndexPromise;
}

export async function resolvePostcardArtPath(country, city) {
  try {
    if (!city) return null;
    const idx = await loadImgIndex();
    const hit = idx[poiSlug(city)];
    if (hit) return hit;
    // Fallback: derive from the country's POI file id (covers any city missing
    // from the index).
    if (!country) return null;
    const fileId = await getCountryFile(country);
    if (!fileId) return null;
    const folder = fileId.replace(/_travel_guide$/, '').replace(/_/g, '-');
    return `${folder}/${folder}-${poiSlug(city)}.webp`;
  } catch { return null; }
}

/** Full URL for an illustration relative path (under the app's TRIP/img). The UI
 *  falls back to a palette card if the asset is absent. */
export function postcardArtUrl(relPath) {
  return relPath ? `./TRIP/img/${relPath}` : null;
}

// ── Redeem code (the free case → printed postcard on losdias.xyz) ────────────

/** A short, human-readable, unambiguous code: WF-XXXX-XXXX (no I/L/O/0/1). */
function generatePostcardCode() {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const n = 8;
  const bytes = (typeof crypto !== 'undefined' && crypto.getRandomValues)
    ? crypto.getRandomValues(new Uint8Array(n))
    : Array.from({ length: n }, () => Math.floor(Math.random() * 256));
  let out = '';
  for (let i = 0; i < n; i++) out += alphabet[bytes[i] % alphabet.length];
  return `WF-${out.slice(0, 4)}-${out.slice(4, 8)}`;
}

/** The losdias.xyz link that redeems a postcard for a printed one. Language is a
 *  path prefix (/es, /en, /fr); only es/fr are non-default, en falls through. */
export function buildRedeemUrl(card, lang = 'en') {
  const short = (lang || 'en').split('-')[0];
  const prefix = ['es', 'fr', 'en'].includes(short) ? short : 'en';
  const params = new URLSearchParams({
    code: card?.code || '',
    city: card?.cityId || '',
  });
  // Carry the illustration's relative path so losdias.xyz can show it without
  // replicating the country-folder/slug logic.
  if (card?.img) params.set('img', card.img);
  return `${POSTCARD_REDEEM_BASE}/${prefix}/postal?${params.toString()}`;
}

/** Buy link for the trip city's postcard (no code → losdias.xyz charges for it).
 *  Always available, even if the gems aren't found. Carries city + illustration. */
export async function buildBuyUrl(country, city, lang = 'en') {
  const short = (lang || 'en').split('-')[0];
  const prefix = ['es', 'fr', 'en'].includes(short) ? short : 'en';
  const params = new URLSearchParams({ city: poiSlug(city || '') });
  const img = await resolvePostcardArtPath(country, city);
  if (img) params.set('img', img);
  return `${POSTCARD_REDEEM_BASE}/${prefix}/postal?${params.toString()}`;
}

// ── Cross-trip "found" set ──────────────────────────────────────────────────

/** Union of every surfaced gem id across ALL trips (active, completed, sealed).
 *  A gem id is a canonical slug, so the same gem found in two trips collapses. */
async function getAllSurfacedIds() {
  const trips = await storageManager.getAllTrips().catch(() => []);
  const s = new Set();
  for (const t of trips) for (const id of (t?.surfacedPois || [])) s.add(id);
  return s;
}

/**
 * The gem status of a city, cross-trip.
 * @returns {Promise<{ cityId, total, found, foundIds:string[], complete:boolean, gems:Array }>}
 *   total === 0 → the city has no authored gems (no constellation, no postcard).
 */
export async function cityGemStatus(country, city) {
  const cityId = poiSlug(city || '');
  const gems = await loadCityHiddenGems(country, city);
  const total = gems.length;
  if (!total) return { cityId, total: 0, found: 0, foundIds: [], complete: false, gems: [] };

  const surfaced = await getAllSurfacedIds();
  const foundIds = gems.filter(g => surfaced.has(g.id)).map(g => g.id);
  const found = foundIds.length;
  return { cityId, total, found, foundIds, complete: found === total, gems };
}

// ── Persisted postcard collection (userProfile store, dedicated key) ─────────

async function loadPostcardsRecord() {
  try {
    await storageManager.initialize();
    const db = storageManager.db;
    return new Promise((resolve) => {
      const tx = db.transaction('userProfile', 'readonly');
      const req = tx.objectStore('userProfile').get(POSTCARDS_KEY);
      req.onsuccess = () => resolve(req.result || { updatedAt: null, cities: [] });
      req.onerror = () => resolve({ updatedAt: null, cities: [] });
    });
  } catch { return { updatedAt: null, cities: [] }; }
}

async function savePostcardsRecord(record) {
  try {
    await storageManager.initialize();
    const db = storageManager.db;
    return new Promise((resolve) => {
      const tx = db.transaction('userProfile', 'readwrite');
      tx.objectStore('userProfile').put(record, POSTCARDS_KEY);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch { return false; }
}

/** The unlocked postcards, newest first. */
export async function getUnlockedPostcards() {
  const rec = await loadPostcardsRecord();
  return [...(rec.cities || [])].sort((a, b) =>
    String(b.unlockedAt || '').localeCompare(String(a.unlockedAt || '')));
}

/** True if this city's postcard is already unlocked. */
export async function isPostcardUnlocked(cityId) {
  const rec = await loadPostcardsRecord();
  return (rec.cities || []).some(c => c.cityId === cityId);
}

/**
 * Unlock a city's postcard (idempotent). Returns true only the FIRST time, so
 * callers can show the unlock moment exactly once.
 */
export async function unlockPostcard(cityId, meta = {}) {
  if (!cityId) return false;
  const rec = await loadPostcardsRecord();
  const cities = rec.cities || [];
  if (cities.some(c => c.cityId === cityId)) return false;
  const card = {
    cityId,
    city: meta.city || cityId,
    country: meta.country || '',
    unlockedAt: new Date().toISOString(),
    code: generatePostcardCode(),   // the entitlement carried in the redeem link
    img: await resolvePostcardArtPath(meta.country, meta.city), // TRIP/img relative path
  };
  cities.push(card);
  await savePostcardsRecord({ updatedAt: new Date().toISOString(), cities });
  window.__cachedPostcards = null;
  return true;
}

/**
 * Evaluate a trip's destination city and unlock its postcard if every gem has
 * been found (cross-trip). Idempotent; safe to call on every gem surfaced.
 * @returns {Promise<{ complete:boolean, newlyUnlocked:boolean, cityId, total, found, city, country }>}
 */
export async function evaluateCityCompletion(trip) {
  const city = trip?.destination?.city;
  const country = trip?.destination?.country;
  if (!city || !country) return { complete: false, newlyUnlocked: false };
  const status = await cityGemStatus(country, city);
  if (!status.complete) return { ...status, newlyUnlocked: false, city, country };
  const newlyUnlocked = await unlockPostcard(status.cityId, { city, country });
  return { ...status, newlyUnlocked, city, country };
}

/**
 * Rebuild the postcard collection from every trip (restore safety). Preserves
 * existing unlock dates; only adds cities that have become complete. Derived,
 * so a .trip import that brings new surfaced gems re-grants the right postcards.
 */
export async function recomputePostcards() {
  const trips = await storageManager.getAllTrips().catch(() => []);
  // Unique destination cities across all trips.
  const cityByKey = new Map(); // cityId -> { city, country }
  for (const t of trips) {
    const city = t?.destination?.city, country = t?.destination?.country;
    if (!city || !country) continue;
    const cityId = poiSlug(city);
    if (!cityByKey.has(cityId)) cityByKey.set(cityId, { city, country });
  }

  const rec = await loadPostcardsRecord();
  const existing = new Map((rec.cities || []).map(c => [c.cityId, c]));

  for (const [cityId, meta] of cityByKey) {
    if (existing.has(cityId)) continue; // keep its original unlockedAt
    const status = await cityGemStatus(meta.country, meta.city);
    if (status.complete) {
      existing.set(cityId, {
        cityId, city: meta.city, country: meta.country,
        unlockedAt: new Date().toISOString(),
        code: generatePostcardCode(),
        img: await resolvePostcardArtPath(meta.country, meta.city),
      });
    }
  }

  const cities = [...existing.values()];
  await savePostcardsRecord({ updatedAt: new Date().toISOString(), cities });
  window.__cachedPostcards = null;
  return cities;
}
